/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – AuthContext
 * ───────────────────────────────────────────────────────────────
 * Firebase Auth + Firestore rol yönetimi (superAdmin, admin, customer)
 * Müşteriler Firebase Auth kullanmaz, Firestore sorgusu ile girer.
 * ═══════════════════════════════════════════════════════════════
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { getSystemSettings, setSystemSettings } from '../services/configService';

// Telefon formatlayıcı (Sadece rakamları tut, baştaki 0'ı at)
export function formatPhone(phone) {
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }
  return cleaned;
}

// ── Context ──
const AuthContext = createContext(null);

// ── Roles ──
export const ROLES = Object.freeze({
  SUPER_ADMIN: 'superAdmin',
  ADMIN: 'admin',
  SUB_ADMIN: 'subAdmin',
  CUSTOMER: 'customer',
  UNAUTHORIZED: 'unauthorized'
});

// ── Permission Map ──
const PERMISSIONS = Object.freeze({
  [ROLES.SUPER_ADMIN]: [
    'hisse:create', 'hisse:read', 'hisse:update', 'hisse:delete',
    'payment:create', 'payment:read', 'payment:update', 'payment:delete',
    'user:manage', 'sms:send', 'report:view', 'finance:edit', 'finance:delete',
  ],
  [ROLES.ADMIN]: [
    'hisse:create', 'hisse:read', 'hisse:update',
    'payment:create', 'payment:read',
    'sms:send', 'report:view',
  ],
  [ROLES.CUSTOMER]: [
    'hisse:read',
    'payment:read',
  ],
});

// ── Provider ──
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [systemSettings, setSystemSettings] = useState(null);

  // ── Firebase System Settings State Listener ──
  useEffect(() => {
    // Initial fetch guarantees creation if doesn't exist
    getSystemSettings().then(initial => setSystemSettings(initial)).catch(err => console.error(err));

    const unsubSettings = onSnapshot(doc(db, 'settings', 'globals'), (docSnap) => {
      if (docSnap.exists()) {
        setSystemSettings(docSnap.data());
      }
    });
    return unsubSettings;
  }, []);

  // ── Firestore'dan kullanıcı profilini çek ──
  const fetchUserProfile = useCallback(async (uid, userAuthObj) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const profile = { uid, ...userDoc.data() };
        
        // ── HESAP ASKIYA ALINMA KONTROLÜ (GÜVENLİK) ──
        if ((profile.isActive === false && profile.role !== ROLES.SUPER_ADMIN) || profile.role === ROLES.UNAUTHORIZED) {
           await firebaseSignOut(auth);
           throw new Error("Hesabınız henüz onaylanmadı veya askıya alındı. Kapalı devre sistem. Lütfen yöneticiyle iletişime geçin.");
        }
        
        setUserProfile(profile);
        return profile;
      }
      
      // Profil yoksa yeni oluştur
      // SUPER ADMIN TANIMLAMA MANTIĞI: Belirli e-posta Süper Admin yapılır
      let assignedRole = ROLES.UNAUTHORIZED; // Güvenlik yaması: Varsayılan Rol artık Unauthorized
      const email = userAuthObj?.email || '';
      if (email.toLowerCase() === 'kaan.ozcan@hotmail.com') {
        assignedRole = ROLES.SUPER_ADMIN;
      }

      const defaultProfile = {
        email: email,
        role: assignedRole,
        isActive: true,
        displayName: userAuthObj?.displayName || email.split('@')[0],
        createdAt: serverTimestamp(),
      };
      await setDoc(userDocRef, defaultProfile);
      const created = { uid, ...defaultProfile, createdAt: new Date() };
      setUserProfile(created);
      return created;
    } catch (err) {
      console.error('[AuthContext] Profil çekme hatası:', err);
      // Fırlatılan hatayı onAuthStateChanged veya frontend yakalayıp ekranda bastıracak.
      throw err;
    }
  }, []);

  // ── Firebase Auth State Listener ──
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
           await fetchUserProfile(user.uid, user);
           setCurrentUser(user);
        } catch (e) {
           // Askıya alınmış hesap veya veritabanı okuma hatası durumu
           setError(e.message);
           setCurrentUser(null);
           setUserProfile(null);
           localStorage.removeItem('customer_session');
        }
      } else {
        setCurrentUser(null);
        // Firebase auth yok, belki LocalStorage'da müşteri oturumu vardır
        const customerSession = localStorage.getItem('customer_session');
        if (customerSession) {
          try {
            const parsedSession = JSON.parse(customerSession);
            setUserProfile(parsedSession);
          } catch (e) {
            localStorage.removeItem('customer_session');
            setUserProfile(null);
          }
        } else {
          setUserProfile(null);
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [fetchUserProfile]);

  // ── Admin Girişi (ID Bazlı) ──
  const signIn = useCallback(async (username, password) => {
    setError(null);
    try {
      // Eğer kullanıcı kaan.ozcan@hotmail.com gibi e-posta ile girmek isterse diye kontrol:
      const loginEmail = username.includes('@') ? username : `${username.toLowerCase().trim()}@hasimoglu.local`;
      const cred = await signInWithEmailAndPassword(auth, loginEmail, password);
      // Profil onAuthStateChanged içinde çekilecek ve isActive ise devam edecek
      return cred.user;
    } catch (err) {
      const msg = getFirebaseErrorMessage(err.code);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  // ── Müşteri Girişi (Firebase Auth OLMADAN, Firestore'dan soyadı+telefon eşleşmesi) ──
  const loginAsCustomer = useCallback(async (phone, lastName) => {
    setError(null);
    try {
      const standardizedPhone = formatPhone(phone);
      if (standardizedPhone.length < 10) {
        throw new Error('Geçerli bir telefon numarası giriniz.');
      }
      
      // shares koleksiyonunda ara
      const sharesRef = collection(db, 'shares');
      const q = query(
        sharesRef, 
        where('customerPhone', '==', standardizedPhone)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('Bu telefon numarasına ait kayıt bulunamadı.');
      }

      // Soyisim eşleşmesi kontrolü (case-insensitive olması için küçük harf kontrolü)
      let matchedShare = null;
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.customerLastName && data.customerLastName.toLocaleLowerCase('tr-TR') === lastName.toLocaleLowerCase('tr-TR')) {
          matchedShare = data;
        }
      });

      if (!matchedShare) {
        throw new Error('Girilen soyadı telefon numarasıyla eşleşmedi.');
      }

      // Eşleşme başarılı, local session oluştur
      const sessionData = {
        role: ROLES.CUSTOMER,
        phone: standardizedPhone,
        lastName: matchedShare.customerLastName,
        firstName: matchedShare.customerFirstName,
      };
      
      localStorage.setItem('customer_session', JSON.stringify(sessionData));
      setUserProfile(sessionData);
      
      return sessionData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // ── Kayıt (Sadece superAdmin yeni kullanıcı oluşturabilir) ──
  const registerUser = useCallback(async (username, password, role = ROLES.ADMIN, displayName = '', assignedBranches = []) => {
    setError(null);
    if (userProfile?.role !== ROLES.SUPER_ADMIN) {
      const msg = 'Bu işlem için yetkiniz yok. Sadece Süper Admin sistem kullanıcısı oluşturabilir.';
      setError(msg);
      throw new Error(msg);
    }
    
    // Gelen ID'yi zorla formata oturtarak e-posta yaratıyoruz
    const sanitizedUsername = username.toLowerCase().trim();
    const email = `${sanitizedUsername}@hasimoglu.local`;

    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const profile = {
        username: sanitizedUsername,     // Yönetim panelinde görünsün diye
        email: email,                    // Mecburi Firebase ID'si
        rawPassword: password,           // SUPER ADMIN ICIN AÇIK ŞİFRE (Pür Patrona Özel)
        role: role,
        isActive: true,                  // Varsayılan olarak aktif başlar
        displayName: displayName || sanitizedUsername,
        assignedBranches: assignedBranches, // Eklenecek şubeler (Kullanıma açık)
        createdAt: serverTimestamp(),
        createdBy: userProfile.uid,
      };
      await setDoc(doc(db, 'users', cred.user.uid), profile);
      return { uid: cred.user.uid, ...profile };
    } catch (err) {
      const msg = getFirebaseErrorMessage(err.code);
      setError(msg);
      throw new Error(msg);
    }
  }, [userProfile]);

  // ── Çıkış (Admin ve Müşteri) ──
  const signOutUser = useCallback(async () => {
    try {
      localStorage.removeItem('customer_session');
      await firebaseSignOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err) {
      console.error('[AuthContext] Çıkış hatası:', err);
      // Yine de local session'ı temizle
      localStorage.removeItem('customer_session');
      setUserProfile(null);
    }
  }, []);

  // ── Yetki Kontrolü ──
  const hasPermission = useCallback((permission) => {
    if (!userProfile?.role) return false;
    const perms = PERMISSIONS[userProfile.role];
    return perms ? perms.includes(permission) : false;
  }, [userProfile]);

  const hasRole = useCallback((role) => {
    return userProfile?.role === role;
  }, [userProfile]);

  const isSuperAdmin = userProfile?.role === ROLES.SUPER_ADMIN;
  const isAdmin = userProfile?.role === ROLES.ADMIN || isSuperAdmin;
  const isCustomer = userProfile?.role === ROLES.CUSTOMER;

  const canEditFinance = isSuperAdmin;
  const canDeleteFinance = isSuperAdmin || userProfile?.permissions?.canDeletePayment;
  const canDeleteShareholder = isSuperAdmin || userProfile?.permissions?.canDeleteShareholder;
  const canEditAnimal = isSuperAdmin || userProfile?.permissions?.canEditAnimal;

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    signIn,
    loginAsCustomer,
    signOut: signOutUser,
    registerUser,
    hasPermission,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isCustomer,
    canEditFinance,
    canDeleteFinance,
    canDeleteShareholder,
    canEditAnimal,
    ROLES,
    systemSettings,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth, AuthProvider içinde kullanılmalıdır.');
  }
  return context;
}

function getFirebaseErrorMessage(code) {
  const messages = {
    'auth/user-not-found': 'Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı.',
    'auth/wrong-password': 'Şifre hatalı. Lütfen tekrar deneyin.',
    'auth/email-already-in-use': 'Bu e-posta adresi zaten kullanılıyor.',
    'auth/weak-password': 'Şifre en az 6 karakter olmalıdır.',
    'auth/invalid-email': 'Geçersiz e-posta adresi.',
    'auth/too-many-requests': 'Çok fazla başarısız deneme. Lütfen bir süre bekleyin.',
    'auth/network-request-failed': 'Ağ bağlantısı hatası. İnternet bağlantınızı kontrol edin.',
    'auth/invalid-credential': 'E-posta veya şifre hatalı.',
  };
  return messages[code] || `Bir hata oluştu (${code}). Lütfen tekrar deneyin.`;
}

export default AuthContext;
